/**
 * Scraper Health Check Utility
 *
 * Checks scraper health using the /api/health endpoint.
 *
 * ⚠️ AUTHENTICATION (Sep 2026). The scrapers are now API-only: every route
 * requires `X-API-Key`, which is the per-scraper key already on each
 * `discovery_scrapers` document. See
 * rightmove-address-finder/docs/CLOZESURETOOLS-API-AUTH-HANDOVER.md.
 *
 * ⚠️ The trap in that change: `/api/health` is the ONE route that still answers
 * without a key — with a bare `{"status":"ok"}`. An unauthenticated check
 * therefore succeeds, reports "healthy", and silently returns `undefined` for
 * scraperId, worker and proxies. A scraper whose key is wrong would look
 * perfectly fine while telling us nothing. `looksAuthenticated()` below exists
 * to make that impossible.
 */

import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

/** HTTP statuses the authenticated API uses, mapped to something readable. */
function describeStatus(status) {
  if (status === 401) return "No API key sent — the scraper needs X-API-Key";
  if (status === 403) return "API key rejected — wrong key for this scraper";
  if (status === 503)
    return "Scraper has no API key configured (misconfigured)";
  return `HTTP ${status}`;
}

/**
 * A successful /api/health body that carries no detail is the unauthenticated
 * response. Treating it as healthy would report a scraper we cannot actually
 * talk to as fine.
 */
function looksAuthenticated(healthData) {
  return Boolean(healthData && healthData.scraperId);
}

/**
 * Check health of a single scraper (basic check)
 * @param {Object} scraper - The scraper object with url and id
 * @param {boolean} checkProxies - Whether to include detailed proxy checks
 * @returns {Promise<Object>} Health check result
 */

export async function checkScraperHealth(scraper, checkProxies = false) {
  try {
    const healthUrl = checkProxies
      ? `${scraper.url}/api/health?checkProxies=true`
      : `${scraper.url}/api/health`;

    // Set timeout for health check (longer if checking proxies)
    const timeout = checkProxies ? 10000 : 5000; // 10s for proxy checks, 5s for basic
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    if (!scraper.apiKey) {
      return {
        success: false,
        status: "unconfigured",
        error: "No API key stored for this scraper",
        checkedAt: new Date(),
      };
    }

    const response = await fetch(healthUrl, {
      method: "GET",
      headers: { "X-API-Key": scraper.apiKey },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const healthData = await response.json();

      // A 200 with no detail means the key was not accepted — see
      // looksAuthenticated. Do NOT report that as healthy.
      if (!looksAuthenticated(healthData)) {
        return {
          success: false,
          status: "unauthenticated",
          error:
            "Scraper answered without detail — the API key was not accepted",
          checkedAt: new Date(),
        };
      }

      return {
        success: true,
        status: "healthy",
        scraperId: healthData.scraperId,
        worker: healthData.worker,
        proxies: healthData.proxies,
        checkedAt: new Date(),
      };
    } else {
      return {
        success: false,
        status: response.status === 503 ? "unconfigured" : "unhealthy",
        error: describeStatus(response.status),
        checkedAt: new Date(),
      };
    }
  } catch (error) {
    let status = "unhealthy";
    let errorMsg = error.message;

    if (error.name === "AbortError") {
      status = "timeout";
      errorMsg = `Health check timed out (${checkProxies ? "10s" : "5s"})`;
    }

    return {
      success: false,
      status,
      error: errorMsg,
      checkedAt: new Date(),
    };
  }
}

/**
 * Check health of an individual proxy
 * @param {string} scraperUrl - The base URL of the scraper
 * @param {string} proxyUrl - The proxy URL to check
 * @param {string} apiKey - The scraper's API key (required since Sep 2026)
 * @returns {Promise<Object>} Proxy health check result
 */
export async function checkProxyHealth(scraperUrl, proxyUrl, apiKey) {
  try {
    if (!apiKey) {
      return {
        success: false,
        healthy: false,
        error: "No API key stored for this scraper",
        checkedAt: new Date(),
      };
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000); // 6 second timeout

    const response = await fetch(`${scraperUrl}/api/proxies/health`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
      body: JSON.stringify({ url: proxyUrl }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const result = await response.json();
      return {
        success: true,
        ...result,
        checkedAt: new Date(),
      };
    } else {
      return {
        success: false,
        healthy: false,
        error: describeStatus(response.status),
        checkedAt: new Date(),
      };
    }
  } catch (error) {
    let errorMsg = error.message;

    if (error.name === "AbortError") {
      errorMsg = "Timeout";
    }

    return {
      success: false,
      healthy: false,
      error: errorMsg,
      checkedAt: new Date(),
    };
  }
}

/**
 * Update scraper health status in Firestore
 * @param {string} scraperId - The Firestore document ID of the scraper
 * @param {Object} healthResult - Result from checkScraperHealth
 * @returns {Promise<void>}
 */
export async function updateScraperHealthStatus(scraperId, healthResult) {
  try {
    const updateData = {
      healthStatus: healthResult.status,
      lastHealthCheck: serverTimestamp(),
      healthError: healthResult.error || null,
    };

    // Add additional health data if available
    if (healthResult.worker) {
      updateData.workerRunning = healthResult.worker.running;
    }

    if (healthResult.proxies) {
      updateData.proxyStats = {
        total: healthResult.proxies.total,
        enabled: healthResult.proxies.enabled,
        disabled: healthResult.proxies.disabled,
        details: healthResult.proxies.details || null,
      };
    }

    await updateDoc(doc(db, "discovery_scrapers", scraperId), updateData);
  } catch (error) {
    console.error(
      `Failed to update health status for scraper ${scraperId}:`,
      error,
    );
    throw error;
  }
}

/**
 * Check health of all scrapers and update Firestore
 * @param {Array} scrapers - Array of scraper objects
 * @param {boolean} checkProxies - Whether to include detailed proxy checks
 * @returns {Promise<Array>} Array of health check results
 */
export async function checkAllScrapersHealth(scrapers, checkProxies = false) {
  const results = await Promise.all(
    scrapers.map(async (scraper) => {
      /**
       * ⚠️ Skip scrapers that report their own health.
       *
       * Since Sep 2026 every scraper writes its health to its own document
       * each 60 seconds. Two of the three are firewalled and http-only, so the
       * browser cannot reach them and this check would write "unreachable"
       * over a healthy box — until its next self-report flipped it back. That
       * flapping is worse than no check at all, because it teaches people the
       * display cannot be trusted.
       */
      if (scraper?.healthDetails?.reportedBy === "scraper") {
        return {
          scraperId: scraper.id,
          scraperName: scraper.name,
          success: scraper.healthStatus === "healthy",
          status: scraper.healthStatus || "unknown",
          selfReported: true,
          checkedAt: scraper.lastHealthCheck || null,
          skipped: "self-reporting",
        };
      }

      const healthResult = await checkScraperHealth(scraper, checkProxies);

      try {
        await updateScraperHealthStatus(scraper.id, healthResult);
      } catch (error) {
        console.error(`Failed to update health for ${scraper.name}:`, error);
      }

      return {
        scraperId: scraper.id,
        scraperName: scraper.name,
        ...healthResult,
      };
    }),
  );

  return results;
}
